import { Component, Input, OnInit } from '@angular/core';
import { FeatherIconComponent } from '../../../shared/components/common/feather-icon/feather-icon.component';
import { NgbActiveModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AdminDashboardService } from '../../../services/admin-dashboard.service';
import { NotificationService } from '../../../services/notification.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { UiNotificationModel } from '../../../models/ui-notification-model';
import { generateRandomId } from '../../../helpers/utils';
import { User } from '../../../models/user';
import { EntityService } from '../../../services/entity.service';
import { Celebrity } from '../../../models/admin.models';
import { ResponseRequest } from '../../../response-request';

interface Domain {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
}

interface Nationality {
  id: number;
  name: string;
}

interface ApiResponse<T> {
  data: T[];
  status: number;
}

@Component({
  selector: 'app-edit-celebrity',
  standalone: true,
  imports: [FeatherIconComponent,NgbNavModule, ReactiveFormsModule, CommonModule],
  templateUrl: './edit-celebrity.component.html'
})
export class EditCelebrityComponent implements OnInit {
  @Input() celebrity!: Celebrity;
  updateForm!: FormGroup;
  isLoading: boolean = false;
  domains: Domain[] = [];
  categories: Category[] = [];
  nationalities: Nationality[] = [];

  constructor(
    public modalServices: NgbActiveModal,
    private fb: FormBuilder,
    private adminService: AdminDashboardService,
    private notificationService: NotificationService,
    private entityService: EntityService
  ) {
    // Ne pas initialiser le formulaire ici car celebrity n'est pas encore disponible
  }

  ngOnInit() {
    // Initialiser le formulaire avec les données de la célébrité
    this.initForm();
    // Charger les données supplémentaires
    this.loadAllData();
    
    // Ajouter un observateur pour suivre l'état du formulaire
    this.updateForm.statusChanges.subscribe(status => {
      console.log('État du formulaire:', status);
      console.log('Valeurs du formulaire:', this.updateForm.value);
      console.log('Erreurs du formulaire:', this.updateForm.errors);
      Object.keys(this.updateForm.controls).forEach(key => {
        const control = this.updateForm.get(key);
        console.log(`Erreurs pour ${key}:`, control?.errors);
      });
    });
  }

  initForm() {
    console.log('Initialisation du formulaire avec les données:', this.celebrity);
    
    // Créer le formulaire sans validation pour commencer
    this.updateForm = this.fb.group({
      prenom: [this.celebrity?.prenom || ''],
      name: [this.celebrity?.name || ''],
      telephone: [this.celebrity?.telephone || ''],
      email: [this.celebrity?.email || ''],
      domain_id: [this.celebrity?.domain_id || ''],
      category_id: [this.celebrity?.category_id || ''],
      nationalite_id: [this.celebrity?.nationalite_id || ''],
      password: ['']
    });
    
    // Ajouter les validateurs après l'initialisation
    this.updateForm.get('prenom')?.setValidators(Validators.required);
    this.updateForm.get('name')?.setValidators(Validators.required);
    this.updateForm.get('telephone')?.setValidators(Validators.required);
    this.updateForm.get('email')?.setValidators([Validators.required, Validators.email]);
    this.updateForm.get('domain_id')?.setValidators(Validators.required);
    this.updateForm.get('category_id')?.setValidators(Validators.required);
    this.updateForm.get('nationalite_id')?.setValidators(Validators.required);
    
    // Mettre à jour l'état de validation
    this.updateForm.updateValueAndValidity();
    
    // Vérifier l'état de validation
    console.log('État de validation du formulaire:', {
      valid: this.updateForm.valid,
      errors: this.updateForm.errors,
      controls: Object.keys(this.updateForm.controls).map(key => ({
        key,
        valid: this.updateForm.get(key)?.valid,
        errors: this.updateForm.get(key)?.errors
      }))
    });
  }

  private async loadAllData() {
    try {
      this.isLoading = true;
      
      const [domainsData, categoriesData, nationalitiesData] = await Promise.all([
        this.entityService.getDatas<Domain>('/domains').toPromise(),
        this.entityService.getDatas<Category>('/categories').toPromise(),
        this.entityService.getDatas<Nationality>('/nationalites').toPromise()
      ]);

      this.domains = domainsData || [];
      this.categories = categoriesData || [];
      this.nationalities = nationalitiesData || [];
      
      console.log('Données chargées:', {
        domains: this.domains,
        categories: this.categories,
        nationalities: this.nationalities
      });
      
      // Mettre à jour le formulaire avec les données de la célébrité
      if (this.celebrity) {
        console.log('Mise à jour du formulaire avec les données de la célébrité:', this.celebrity);
        
        // Vérifier si les IDs existent dans les listes
        const domainExists = this.domains.some(d => d.id === this.celebrity.domain_id);
        const categoryExists = this.categories.some(c => c.id === this.celebrity.category_id);
        const nationalityExists = this.nationalities.some(n => n.id === this.celebrity.nationalite_id);
        
        // S'assurer que toutes les valeurs sont définies et valides
        const formValues = {
          prenom: this.celebrity.prenom || '',
          name: this.celebrity.name || '',
          telephone: this.celebrity.telephone || '',
          email: this.celebrity.email || '',
          domain_id: domainExists ? this.celebrity.domain_id : (this.domains[0]?.id || ''),
          category_id: categoryExists ? this.celebrity.category_id : (this.categories[0]?.id || ''),
          nationalite_id: nationalityExists ? this.celebrity.nationalite_id : (this.nationalities[0]?.id || ''),
          password: ''
        };
        
        console.log('Valeurs du formulaire à appliquer:', formValues);
        
        // Mettre à jour le formulaire
        this.updateForm.patchValue(formValues);
        
        // Forcer la validation du formulaire
        Object.keys(this.updateForm.controls).forEach(key => {
          const control = this.updateForm.get(key);
          control?.markAsTouched();
        });
        
        // Vérifier l'état de validation après la mise à jour
        console.log('État de validation du formulaire après mise à jour:', {
          valid: this.updateForm.valid,
          errors: this.updateForm.errors,
          controls: Object.keys(this.updateForm.controls).map(key => ({
            key,
            valid: this.updateForm.get(key)?.valid,
            errors: this.updateForm.get(key)?.errors,
            value: this.updateForm.get(key)?.value
          }))
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      this.notificationService.error('Erreur lors du chargement des données');
    } finally {
      this.isLoading = false;
    }
  }

  submit(event: Event) {
    event.preventDefault();
    
    // Vérifier l'état de validation avant la soumission
    console.log('État de validation du formulaire avant soumission:', {
      valid: this.updateForm.valid,
      errors: this.updateForm.errors,
      controls: Object.keys(this.updateForm.controls).map(key => ({
        key,
        valid: this.updateForm.get(key)?.valid,
        errors: this.updateForm.get(key)?.errors
      }))
    });
    
    // Forcer la validation de tous les champs
    Object.keys(this.updateForm.controls).forEach(key => {
      const control = this.updateForm.get(key);
      control?.markAsTouched();
    });
    
    // Vérifier à nouveau l'état de validation
    console.log('État de validation du formulaire après marquage comme touché:', {
      valid: this.updateForm.valid,
      errors: this.updateForm.errors,
      controls: Object.keys(this.updateForm.controls).map(key => ({
        key,
        valid: this.updateForm.get(key)?.valid,
        errors: this.updateForm.get(key)?.errors
      }))
    });
    
    // Soumettre le formulaire même s'il n'est pas valide (pour le débogage)
    this.isLoading = true;
    
    // Créer un objet avec les valeurs du formulaire
    const formData = this.updateForm.value;
    
    // Ne pas envoyer le mot de passe s'il est vide
    if (!formData.password) {
      delete formData.password;
    }
    
    console.log('Soumission du formulaire avec les données:', formData);
    
    this.entityService.updateDataWithPost('/users', this.celebrity.id, formData)
      .subscribe({
        next: () => {
          this.notificationService.success('Célébrité mise à jour avec succès');
          this.modalServices.close('success');
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour:', error);
          this.notificationService.error('Erreur lors de la mise à jour');
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  displayDomains(){
    this.entityService.getDatas('/domains').subscribe({
      next: (data:any) => {
        this.domains = data.data;
        console.log(this.domains);
      }
    });
  }

  emit(){
    const notification:UiNotificationModel = {
      _id:generateRandomId(),
      title: "Test",
      message: "This is a test notification",
      status: "danger",
      timeout: 2000
    }
    this.notificationService.emit(notification)
    console.log('notification:', notification.message);
  }
}
